import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Camera, Search, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { apiFetch } from "@/lib/api";

interface SearchItem {
  type: "asset" | "material" | "request" | "license";
  id: string;
  title: string;
  subtitle: string | null;
  href: string;
}

interface SearchGroup {
  type: string;
  label: string;
  items: SearchItem[];
}

interface SearchResponse {
  query: string;
  groups: SearchGroup[];
}

interface BarcodeDetectorLike {
  detect(source: HTMLVideoElement): Promise<{ rawValue: string }[]>;
}

type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

export function GlobalSearch({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  const results = useQuery({
    queryKey: ["global-search", debounced],
    queryFn: () => apiFetch<SearchResponse>(`/api/v1/search?q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.length >= 2,
  });

  const stopScan = useCallback(() => {
    if (timerRef.current) window.clearInterval(timerRef.current);
    timerRef.current = null;
    streamRef.current?.getTracks().forEach((track) => {
      track.stop();
    });
    streamRef.current = null;
    setScanning(false);
  }, []);

  useEffect(() => {
    if (!open) stopScan();
  }, [open, stopScan]);

  async function startScan() {
    const Ctor = (window as unknown as { BarcodeDetector?: BarcodeDetectorCtor }).BarcodeDetector;
    if (!Ctor) {
      toast.error("当前浏览器不支持 BarcodeDetector，请使用 Chrome/Edge 或手动输入编号");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      setScanning(true);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      const detector = new Ctor({ formats: ["qr_code", "code_128", "code_39", "ean_13"] });
      timerRef.current = window.setInterval(async () => {
        if (!videoRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          const raw = codes[0]?.rawValue;
          if (raw) {
            setQuery(raw);
            toast.success(`已识别：${raw}`);
            stopScan();
          }
        } catch {
          // frame not ready
        }
      }, 500);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "无法打开摄像头");
      stopScan();
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) stopScan();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>全局搜索</DialogTitle>
          <DialogDescription>
            按资产编号、序列号、主机名、财务卡片、物料编码或申请编号查找。
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              className="pl-9"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="输入至少 2 个字符，例如 1A1A 或 ThinkPad"
            />
          </div>
          {scanning ? (
            <Button variant="outline" onClick={stopScan}>
              <X className="h-4 w-4" />
              停止扫码
            </Button>
          ) : (
            <Button variant="outline" onClick={startScan}>
              <Camera className="h-4 w-4" />
              扫码
            </Button>
          )}
        </div>
        {scanning ? (
          <video
            ref={videoRef}
            className="max-h-64 w-full rounded-md bg-black object-contain"
            muted
            playsInline
          />
        ) : null}
        <div className="max-h-[50vh] space-y-4 overflow-auto">
          {debounced.length < 2 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">输入关键词开始搜索。</p>
          ) : results.isLoading ? (
            <p className="py-6 text-center text-sm text-muted-foreground">搜索中…</p>
          ) : (results.data?.groups ?? []).length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">没有匹配结果。</p>
          ) : (
            (results.data?.groups ?? []).map((group) => (
              <div key={group.type}>
                <p className="mb-2 text-xs font-medium text-muted-foreground">{group.label}</p>
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <button
                      key={`${item.type}-${item.id}`}
                      type="button"
                      className="flex w-full items-center justify-between gap-3 rounded-md border border-border px-3 py-2 text-left text-sm hover:bg-muted/60"
                      onClick={() => {
                        onOpenChange(false);
                        navigate({ to: item.href as never });
                      }}
                    >
                      <span className="min-w-0">
                        <span className="block truncate font-medium">{item.title}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {item.subtitle ?? "—"}
                        </span>
                      </span>
                      <Badge variant="secondary">{group.label}</Badge>
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
